import { PropsWithChildren, createContext, useCallback, useContext, useState } from 'react'
import { IndicationByIndicationFlow, IndicationFlowContextData, ProposalByIndicationFlow, ContractByIndicationFlow, CustomerByIndicationFlow } from './types'
import { useParams } from 'react-router-dom'
import { useGetProposalAndIndicationByIdQuery, useCustomerGetQuery } from '~/graphql/types'
import { toast } from 'react-toastify'

const IndicationFlowContext = createContext({} as IndicationFlowContextData)

export const IndicationFlowProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { id } = useParams()

  const [stepper, setStepper] = useState(0)
  const [selectedIndication, setSelectedIndication] = useState<null | IndicationByIndicationFlow>(null)
  const [selectedProposal, setSelectedProposal] = useState<null | ProposalByIndicationFlow>(null)
  const [selectedContract, setSelectedContract] = useState<null | ContractByIndicationFlow>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<null | CustomerByIndicationFlow>(null)
  const isPf = selectedProposal?.customerRef.personType === 'pf'

  const { loading: getIndcationAndProposalIsLoading, refetch: refetchProposalAndIndication } = useGetProposalAndIndicationByIdQuery({
    variables: {
      id: id || ''
    },
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      if (data.getIndicationById._id) {
        setSelectedIndication(data.getIndicationById)
      }
      if (data.getIndicationRelatedEntitiesById.proposal?._id) {
        setSelectedProposal(data.getIndicationRelatedEntitiesById.proposal)
      }
      if (data.getIndicationRelatedEntitiesById.contract?._id) {
        setSelectedContract(data.getIndicationRelatedEntitiesById.contract)
      }
    },
    onError(error) {
      toast.error(error.message)
    },
  })

  const { loading: getCustomerIsLoading } = useCustomerGetQuery({
    variables: {
      id: selectedContract?.customerId || ''
    },
    skip: !selectedContract?.customerId,
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      if (data.customerGet?._id) {
        setSelectedCustomer(data.customerGet)
      }
    },
    onError(error) {
      toast.error(error.message)
    }
  })

  const goToPage = useCallback((page: number) => {
    setStepper(page)
  }, [])

  return (
    <IndicationFlowContext.Provider value={{
      setStepper,
      stepper,
      goToPage,
      getIndcationAndProposalIsLoading,
      selectedIndication,
      selectedProposal,
      selectedContract,
      selectedCustomer,
      setSelectedIndication,
      setSelectedProposal,
      setSelectedContract,
      setSelectedCustomer,
      isPf,
      getCustomerIsLoading,
      refetchProposalAndIndication
    }}>
      {children}
    </IndicationFlowContext.Provider>
  )
}

export const useIndicationFlow = () => useContext(IndicationFlowContext)
